import React, { useEffect, useRef, useState, useCallback } from 'react';
import { Box, ToggleButton, Tooltip } from '@mui/material';
import LocationSearchingIcon from '@mui/icons-material/LocationSearching';
import haversine from 'haversine';
import { useTheme } from '@mui/material/styles';

const GoogleMap = ({ position, thingModel, selectedStopId, onStopSelect }) => {
    const mapRef = useRef(null);
    const mapInstance = useRef(null);
    const [follow, setFollow] = useState(false);
    const markersRef = useRef({ bus: null, stops: [] });
    const polylinesRef = useRef({ covered: null, remain: null });
    const infoWindowRef = useRef(null);
    const mapInitialized = useRef(false);
    const theme = useTheme();


    // Decode Google polyline
    const decodePolyline = (encoded) => {
        if (!encoded?.trim()) return [];
        const poly = [];
        let index = 0,
            len = encoded.length,
            lat = 0,
            lng = 0;

        try {
            while (index < len) {
                let b,
                    shift = 0,
                    result = 0;
                do {
                    b = encoded.charCodeAt(index++) - 63;
                    result |= (b & 0x1f) << shift;
                    shift += 5;
                } while (b >= 0x20);
                const dlat = result & 1 ? ~(result >> 1) : result >> 1;
                lat += dlat;

                shift = 0;
                result = 0;
                do {
                    b = encoded.charCodeAt(index++) - 63;
                    result |= (b & 0x1f) << shift;
                    shift += 5;
                } while (b >= 0x20);
                const dlng = result & 1 ? ~(result >> 1) : result >> 1;
                lng += dlng;

                poly.push({ lat: lat / 1e5, lng: lng / 1e5 });
            }
        } catch (e) {
            console.error('Polyline decode error:', e);
        }
        return poly;
    };

    const applyStyles = (element, styles) => {
        Object.entries(styles).forEach(([key, value]) => {
            element.style[key] = value;
        });
    };

    const initMap = useCallback(() => {
        if (!window.google?.maps || !mapRef.current) return;

        const map = new window.google.maps.Map(mapRef.current, {
            zoom: 15,
            center: { lat: 20.5937, lng: 78.9629 },
            mapTypeId: 'roadmap',
            disableDefaultUI: true,
            zoomControl: false,
            gestureHandling: 'greedy',
            styles: [{ featureType: 'poi', elementType: 'labels', stylers: [{ visibility: 'off' }] }],
        });

        mapInstance.current = map;
        infoWindowRef.current = new window.google.maps.InfoWindow();

        // === Custom Zoom Controls ===
        const zoomControl = document.createElement('div');
        zoomControl.style.margin = '20px';

        const zoomIn = document.createElement('button');
        const zoomOut = document.createElement('button');

        const buttonStyle = {
            width: '40px',
            height: '40px',
            borderRadius: '12px',
            background: theme.palette.background.paper,
            border: 'none',
            fontSize: '20px',
            boxShadow: '0 3px 6px rgba(0,0,0,0.25)',
            cursor: 'pointer',
            color: theme.palette.text.primary,
        };

        applyStyles(zoomIn, { ...buttonStyle, marginBottom: '8px' });
        zoomIn.innerHTML = '+';

        applyStyles(zoomOut, { ...buttonStyle, marginBottom: '0' });
        zoomOut.innerHTML = '−';

        zoomIn.onclick = () => map.setZoom(map.getZoom() + 1);
        zoomOut.onclick = () => map.setZoom(map.getZoom() - 1);

        [zoomIn, zoomOut].forEach((btn) => {
            btn.onmouseover = () => {
                btn.style.background = theme.palette.primary.main;
                btn.style.color = theme.palette.primary.contrastText;
            };
            btn.onmouseout = () => {
                btn.style.background = theme.palette.background.paper;
                btn.style.color = theme.palette.text.primary;
            };
        });

        zoomControl.appendChild(zoomIn);
        zoomControl.appendChild(zoomOut);
        map.controls[window.google.maps.ControlPosition.RIGHT_BOTTOM].push(zoomControl);

        // Close info window on map click
        map.addListener('click', () => {
            infoWindowRef.current.close();
        });

        return map;
    }, [theme]);

    // Load Google Maps script
    useEffect(() => {
        if (!window.google?.maps) {
            const script = document.createElement('script');
            script.src = `https://maps.googleapis.com/maps/api/js?key=AIzaSyDw39yxlkY6aDVDhqj-W0wBblVKt_mw7xI&libraries=geometry`;
            script.async = true;
            script.onload = () => initMap();
            document.head.appendChild(script);
            return () => {
                document.head.removeChild(script);
            };
        } else {
            initMap();
        }
    }, []);

    // Update map markers and polylines
    useEffect(() => {
        if (!mapInstance.current || !position) return;

        const lat = position.latitude ?? position.lat;
        const lng = position.longitude ?? position.lng;
        if (lat == null || lng == null) return;

        const pos = { lat, lng };

        // === Bus Marker ===
        if (!markersRef.current.bus) {
            markersRef.current.bus = new window.google.maps.Marker({
                map: mapInstance.current,
                icon: {
                    url: require('../../../assets/ship.png'),
                    scaledSize: new window.google.maps.Size(48, 48),
                    anchor: new window.google.maps.Point(24, 48),
                },
            });
        }
        markersRef.current.bus.setPosition(pos);

        // ✅ Only center on the bus marker once (first time)
        if (!mapInitialized.current) {
            mapInstance.current.setCenter(pos);
            mapInstance.current.setZoom(16);
            mapInitialized.current = true;
        }


        // === Stop Markers ===
        const stops = thingModel.stops || {};
        const stopArray = Object.values(stops)
            .filter((s) => s.location?.latitude && s.location?.longitude)
            .sort((a, b) => (a.index ?? 0) - (b.index ?? 0));

        const existingMarkers = markersRef.current.stops.reduce((acc, marker) => {
            acc[marker.stopId] = marker;
            return acc;
        }, {});

        const newMarkers = [];

        stopArray.forEach((stop, i) => {
            const stopPos = { lat: stop.location.latitude, lng: stop.location.longitude };
            const isSelected = selectedStopId === stop.id;
            let marker = existingMarkers[stop.id];

            if (!marker) {
                marker = new window.google.maps.Marker({
                    position: stopPos,
                    map: mapInstance.current,
                    icon: {
                        url: require('../../../assets/marker_stop.png'),
                        scaledSize: new window.google.maps.Size(36, 36),
                        anchor: new window.google.maps.Point(18, 36),
                        labelOrigin: new window.google.maps.Point(18, 14),
                    },
                    label: {
                        text: `${i + 1}`,
                        color: 'white',
                        fontSize: '12px',
                        fontWeight: 'bold',
                    },
                    zIndex: isSelected ? 1000 : 100,
                });

                marker.stopId = stop.id;

                marker.addListener('click', () => {
                    onStopSelect(stop.id);
                    infoWindowRef.current.setContent(`<strong>${stop.locationName}</strong>`);
                    infoWindowRef.current.open(mapInstance.current, marker);
                });
            } else {
                marker.setPosition(stopPos);
                marker.setZIndex(isSelected ? 1000 : 100);
            }

            newMarkers.push(marker);
        });

        // Remove old markers that no longer exist
        markersRef.current.stops.forEach((marker) => {
            if (!stopArray.find((s) => s.id === marker.stopId)) {
                marker.setMap(null);
            }
        });

        markersRef.current.stops = newMarkers;


        // === Polyline (Route) ===
        const poly = thingModel.currentJourney?.wayPointsPolyLine;
        if (poly) {
            const pts = decodePolyline(poly);
            if (pts.length > 1) {
                let bestIdx = 0;
                let bestDist = Infinity;
                pts.forEach((p, i) => {
                    const d = haversine(p, { latitude: lat, longitude: lng }, { unit: 'meter' });
                    if (d < bestDist) {
                        bestDist = d;
                        bestIdx = i;
                    }
                });

                const covered = pts.slice(0, bestIdx + 1);
                const remain = pts.slice(bestIdx);

                // Covered path
                if (polylinesRef.current.covered) polylinesRef.current.covered.setMap(null);
                polylinesRef.current.covered = new window.google.maps.Polyline({
                    path: covered,
                    strokeColor: '#2bbf9f',
                    strokeOpacity: 1,
                    strokeWeight: 5,
                    map: mapInstance.current,
                });

                // Remaining path
                if (polylinesRef.current.remain) polylinesRef.current.remain.setMap(null);
                polylinesRef.current.remain = new window.google.maps.Polyline({
                    path: remain,
                    strokeColor: '#f04c31',
                    strokeOpacity: 1,
                    strokeWeight: 5,
                    map: mapInstance.current,
                });
            }
        } else {
            [polylinesRef.current.covered, polylinesRef.current.remain].forEach((p) => p?.setMap(null));
        }

        // === Follow Mode ===
        if (follow) {
            mapInstance.current.panTo(pos);
            mapInstance.current.setZoom(16);
        }

    }, [position, thingModel, selectedStopId, onStopSelect, follow]);

    const toggleFollow = () => {
        const next = !follow;
        setFollow(next);
        if (next && position && mapInstance.current) {
            const pos = { lat: position.latitude ?? position.lat, lng: position.longitude ?? position.lng };
            mapInstance.current.panTo(pos);
            mapInstance.current.setZoom(16);
        }
    };

    return (
        <Box sx={{ position: 'relative', width: '100%', height: '70vh' }}>
            <Box
                ref={mapRef}
                sx={{
                    width: '100%',
                    height: '100%',
                    borderRadius: 2,
                    overflow: 'hidden',
                    boxShadow: 1,
                }}
            />
            <Tooltip title={follow ? 'Following' : 'Follow vehicle'} placement="left">
                <ToggleButton
                    value="follow"
                    selected={follow}
                    onChange={toggleFollow}
                    sx={{
                        position: 'absolute',
                        top: 16,
                        right: 16,
                        zIndex: 10,
                        bgcolor: 'background.paper',
                        '&.Mui-selected': { bgcolor: 'primary.main', color: 'white' },
                        '&:hover': { bgcolor: follow ? 'primary.dark' : 'grey.300' },
                        borderRadius: 2,
                        minWidth: 48,
                        height: 48,
                    }}
                >
                    <LocationSearchingIcon />
                </ToggleButton>
            </Tooltip>
        </Box>
    );
};

export default GoogleMap;
